/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import Cookies from 'js-cookie';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import type { StoredAccountRegistrationPayload } from './account.types';
import type { RecaptchaRegion } from '../../recaptcha/recaptcha.types';

/**
 * Getters and setters for the "temporary" token coming from making the initial login API
 * call. This token is required for the mfa code POST call, and replaces what was
 * previously an axios interceptor because it was problematic for usage in widget
 */

const temporaryTokenKey = 'temporary-token'; // matches old implementation

export function storeTemporaryToken(token: string) {
    window.sessionStorage.setItem(temporaryTokenKey, token);
}

export function getTemporaryToken() {
    return window.sessionStorage.getItem(temporaryTokenKey);
}

export function removeTemporaryToken() {
    window.sessionStorage.removeItem(temporaryTokenKey);
}

/**
 * Getters and setters for new account registration payload
 * These are used to store the token and recaptcha type in session storage
 * so that they can be used by the resend email button in the VerifyEmailForm component
 */

const accountRegistrationPayloadKey = 'auth-account-registration';

export function storeAccountRegistrationPayload({
    email,
    firstName,
    lastName,
    cellPhone,
    optIn,
}: {
    email: string;
    firstName: string;
    lastName: string;
    cellPhone: string;
    optIn: boolean;
    token?: string;
    recaptchaRegion?: RecaptchaRegion;
}) {
    try {
        window.sessionStorage.setItem(
            accountRegistrationPayloadKey,
            JSON.stringify({
                email,
                firstName,
                lastName,
                cellPhone,
                optIn,
            })
        );
    } catch (error) {
        // do nothing
    }
}

export function getAccountRegistrationPayload() {
    try {
        const raw = window.sessionStorage.getItem(accountRegistrationPayloadKey);

        if (raw) {
            return JSON.parse(raw) as StoredAccountRegistrationPayload;
        }
        return null;
    } catch (error) {
        // do nothing
        return null;
    }
}

export function removeAccountRegistrationPayload() {
    window.sessionStorage.removeItem(accountRegistrationPayloadKey);
}

/**
 * Getters and setters for email confirmed token needed to save the user's password
 */

const emailConfirmedTokenKey = 'auth-email-confirmed-token';

export function storeEmailConfirmedToken(token: string) {
    window.sessionStorage.setItem(emailConfirmedTokenKey, token);
}

export function getEmailConfirmedToken() {
    return window.sessionStorage.getItem(emailConfirmedTokenKey);
}

export function removeEmailConfirmedToken() {
    window.sessionStorage.removeItem(emailConfirmedTokenKey);
}

/**
 * Clean up all session storage items used by any auth related API calls
 */
export function externalAccountApiCleanup() {
    removeTemporaryToken();
    removeAccountRegistrationPayload();
    removeEmailConfirmedToken();
}

export async function getFingerprint() {
    const fp = await FingerprintJS.load({ monitoring: false });
    const result = await fp.get();
    return result.visitorId;
}

const conversationIdKey = 'conversation_id';

export function getConversationId(): string | null {
    try {
        const cookieValue = Cookies.get(conversationIdKey);
        if (cookieValue) {
            return cookieValue;
        }
        return window.sessionStorage.getItem(conversationIdKey);
    } catch {
        return null;
    }
}

// cSpell:ignore fingerprintjs
